import type { BlockDefinition, ImageAspect, ImageProps } from '../types';
import { escapeHtml, safeUrl } from '../html';

const ASPECT_VALUES: ImageAspect[] = ['auto', '1/1', '3/4', '9/16', '4/3', '16/9', '3/1'];

export const image: BlockDefinition<ImageProps> = {
  type: 'image',
  meta: {
    label: 'Image',
    icon: '🖼️',
    category: 'media',
    description: 'Photo or graphic with sizing + aspect controls.',
  },
  defaults: () => ({
    src: '',
    alt: '',
    caption: '',
    shape: 'rounded',
    widthPct: 100,
    aspectRatio: 'auto',
    align: 'center',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<ImageProps>;
    let widthPct = typeof i.widthPct === 'number' ? Math.floor(i.widthPct) : 100;
    if (!Number.isFinite(widthPct)) widthPct = 100;
    if (widthPct < 10) widthPct = 10;
    if (widthPct > 100) widthPct = 100;
    return {
      src: typeof i.src === 'string' ? i.src.slice(0, 2000) : '',
      alt: typeof i.alt === 'string' ? i.alt.slice(0, 200) : '',
      caption: typeof i.caption === 'string' ? i.caption.slice(0, 200) : '',
      shape: i.shape === 'square' ? 'square' : 'rounded',
      widthPct,
      aspectRatio: ASPECT_VALUES.includes(i.aspectRatio as ImageAspect)
        ? (i.aspectRatio as ImageAspect)
        : 'auto',
      align: ['left', 'center', 'right'].includes(i.align as string)
        ? (i.align as ImageProps['align'])
        : 'center',
    };
  },
  render(props) {
    const widthPct = typeof props.widthPct === 'number' ? props.widthPct : 100;
    const aspect = props.aspectRatio && props.aspectRatio !== 'auto' ? props.aspectRatio : 'auto';
    const align = props.align || 'center';
    const radius = props.shape === 'square' ? '0' : 'var(--radius-card)';

    // Margin trick for horizontal alignment without flex (so the image
    // block plays nicely inline with sibling blocks at any width).
    const margin =
      align === 'center'
        ? '0 auto'
        : align === 'right'
          ? '0 0 0 auto'
          : '0 auto 0 0';

    if (!props.src) {
      // Empty state — invites action when seen in the editor canvas.
      // Respects the configured dimensions so the user can see exactly
      // where (and how big) their image will land before uploading.
      const placeholderAspect = aspect === 'auto' ? '16/9' : aspect;
      return `<div class="pb-block pb-image">
  <div style="width:${widthPct}%;aspect-ratio:${placeholderAspect};background:rgba(0,0,0,0.06);border:2px dashed rgba(0,0,0,0.15);border-radius:${radius};display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;color:var(--text);opacity:0.55;font-size:14px;margin:${margin};">
    <span style="font-size:28px;">🖼️</span>
    <span style="font-weight:600;">Click to add an image</span>
    <span style="font-size:11px;opacity:0.7;">Use the panel on the right to upload or paste a URL</span>
  </div>
</div>`;
    }

    // Image styles: width controls the size; aspect-ratio (when not auto)
    // forces the shape with object-fit:cover so non-matching photos crop
    // rather than stretch.
    const imgStyles = [
      'display:block',
      `width:${widthPct}%`,
      `border-radius:${radius}`,
      `margin:${margin}`,
    ];
    if (aspect !== 'auto') {
      imgStyles.push(`aspect-ratio:${aspect}`);
      imgStyles.push('object-fit:cover');
      imgStyles.push('height:auto');
    } else {
      imgStyles.push('height:auto');
    }

    // Caption alignment matches image alignment so they read as a pair.
    const captionAlign = align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';
    const cap = props.caption
      ? `<div style="text-align:${captionAlign};font-size:13px;color:var(--text);opacity:0.65;margin-top:6px;">${escapeHtml(props.caption)}</div>`
      : '';

    return `<div class="pb-block pb-image">
  <img src="${safeUrl(props.src)}" alt="${escapeHtml(props.alt)}" style="${imgStyles.join(';')}" loading="lazy" decoding="async" />
  ${cap}
</div>`;
  },
};
